/** One poll's view of the saved meeting, as far as the transcript is concerned. */
export interface TranscriptPollSnapshot {
  /** Segments the latest read returned. */
  transcriptLength: number
  /** Segments the previous read returned, or a negative number on the first read. */
  previousTranscriptLength: number
  /** Saved duration; zero means nothing was recorded, so no transcript is coming. */
  durationSeconds: number
  /** True while this meeting still has a pending client-side save in flight. */
  hasPendingSave: boolean
}

/**
 * Whether the Meeting Minutes transcript poll can stop.
 *
 * A non-empty transcript is not a complete one. A native session is projected
 * more than once — the End Meeting `PATCH` runs it while ingestion may still be
 * in flight, and reconciliation runs it again afterwards — so stopping at the
 * first read that saw any segment left the open page showing a partial
 * transcript until it was reloaded (BRAI-367). Two consecutive reads agreeing on
 * the count is the signal that the projection has settled.
 *
 * The two escapes are unchanged: a meeting with no recorded duration has no
 * transcript to wait for, and one with no pending save is not mid-write at all.
 *
 * @returns True when polling should stop.
 */
export function isTranscriptPollComplete(snapshot: TranscriptPollSnapshot): boolean {
  const { transcriptLength, previousTranscriptLength, durationSeconds, hasPendingSave } = snapshot
  const settled = transcriptLength === previousTranscriptLength
  return (transcriptLength > 0 && settled) || durationSeconds === 0 || !hasPendingSave
}
